import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useState } from 'react';
import ApiEndpointBlock from './ApiEndpointBlock'; // adjust path if needed

const placeOrderPayload = {
  "orders": [
    {
      "productDTOList": [
        {
          "organisationName": "Inboxlogy_Order",
          "emailProvider": "GOOGLE WORKSPACE",
          "domain": "myoutinstanty.ai",
          "email": "ui@myoutinstanty.com",
          "firstName": "Joe",
          "lastName": "Smith",
          "profilePicture": "https://drive.google.com/file/d/1LDRlyZaZTF-bxHjBDEiL2obMxYHtf3YT/view?usp=sharing",
          "fwdDomain": "instantly.ai",
          "preferredPassword": "null",
          "recoveryEmail": "test@example.com",
          "workspaceAdminFlag": true,
        },
        {
          "organisationName": "Inboxlogy_Order",
          "emailProvider": "MICROSOFT",
          "domain": "myinstanty.ai",
          "email": "ui@myinstanty.com",
          "firstName": "Mac",
          "lastName": "Ballad",
          "profilePicture": "https://drive.google.com/file/d/1LDRlyZaZTF-bxHjBDEiL2obMxYHtf3YT/view?usp=sharing",
          "fwdDomain": "instantly.ai",
          "preferredPassword": "null",
          "recoveryEmail": "test@example.com",
          "workspaceAdminFlag": false,
        }
      ],
      "customerId": 882,
      "organisationName": "Inboxlogy_Order",
      "userId": "67caa687b912cb6fbeaf841b",
      "userName": "sample@instantly.com",
    }
  ],
  "flatOrders": null,
  "summary": null,
  "totalCount": 0,
  "orderCountByStatus": 0,
  "paymentSummery": null
};

const fieldDescriptions = [
  { field: "organisationName", type: "string", required: true, description: "Name of the organization placing the order." },
  { field: "emailProvider", type: "string", required: true, description: "Email service provider used (e.g., Google Workspace)." },
  { field: "domain", type: "string", required: true, description: "Domain name to be registered." },
  { field: "email", type: "string", required: true, description: "Primary email address to assign." },
  { field: "firstName", type: "string", required: true, description: "User's first name." },
  { field: "lastName", type: "string", required: true, description: "User's last name." },
  { field: "fwdDomain", type: "string", required: false, description: "Forwarding domain (optional)." },
  { field: "preferredPassword", type: "string", required: false, description: "Preferred password (if any)." },
  { field: "recoveryEmail", type: "string", required: false, description: "Backup recovery email." },
  { field: "workspaceAdminFlag", type: "boolean", required: false, description: "Whether to assign admin privileges." },
  { field: "isActive", type: "boolean", required: true, description: "Indicates if the order is active." },
  { field: "createdDate", type: "datetime", required: false, description: "Timestamp of record creation." },
  { field: "modifiedDate", type: "datetime", required: false, description: "Last modified timestamp." }
];

const ExposedAPI = () => {
  const [response, setResponse] = useState(null);
  const [balance, setBalance] = useState(250);

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>📘 Inboxlogy API Flow</Typography>

      {/* 👇 GoDaddy-style base URL section */}
      <Box mb={4}>
        <Typography variant="h6" gutterBottom>
          Here is the Orders API. Try it out below.
        </Typography>
        <Typography variant="body1" gutterBottom>
          You may use the following base URLs to test calls to the endpoints below:
        </Typography>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>
            <Typography variant="body2">
              For the <strong>Sandbox environment</strong>, use test keys with the following base URL:&nbsp;
              <a href="https://staging.demandlogy.com/api" target="_blank" rel="noreferrer">
                https://staging.demandlogy.com/api
              </a>
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mt: 1 }}>
              For the <strong>Production environment</strong>, use your live credentials with the base URL:&nbsp;
              <a href="https://app.demandlogy.com/api" target="_blank" rel="noreferrer">
                https://app.demandlogy.com/api
              </a>
            </Typography>
          </li>
        </ul>
      </Box>

      {/* Login API */}
      <ApiEndpointBlock
        method="POST"
        path="/login"
        description="Authenticate a user and receive a JWT token"
      >
        <Typography variant="subtitle2">Payload:</Typography>
        <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: 6 }}>
          {`{
  "email": "<string>",
  "password": "<string>",
}`}
        </pre>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Response:</Typography>
        <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: 6, overflowX: 'auto' }}>
          {`{
  "id": "<string>",
  "customerId": "<long>",
  "userName": "<string>",
  "firstName": "<string>",
  "lastName": "<string>",
  "roleName": "<string>",
  "roleId": "<string>",
  "token": "<string>",
  "status": "<integer>",
  "message": "<string>",
  "userType": "<string>"
}`}
        </pre>
      </ApiEndpointBlock>

      {/* Signup API */}
      <ApiEndpointBlock
        method="POST"
        path="/signup"
        description="Create a new user account"
      >
        <Typography variant="subtitle2">Payload:</Typography>
        <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: 6, overflowX: 'auto' }}>
          {`{
  "email": "<string>",
  "password": "<string>",
  "firstName": "<string>",
  "lastName": "<string>",
  "phone": "<string>",
  "companyName": "<string>",
  "isEmailVerified": "<boolean>",
  "isTermsAccepted": "<boolean>",
  "isMarketingUpdateFlag": "<boolean>",
  "profileComplete": "<boolean>"
}`}
        </pre>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Response:</Typography>
        <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: 6, overflowX: 'auto' }}>
          {`{
  "user": {
    "id": "<string>",
    "customerId": "<string>",
    "customerStatus": "<string>",
    "firstName": "<string>",
    "lastName": "<string>",
    "profilePicture": "<string>",
    "email": "<string>",
    "billingEmailAddress": "<string>",
    "password": "<string>",
    "mobile": "<string>",
    "whatsApp": "<string>",
    "phone": "<string>",
    "companyName": "<string>",
    "companyWebsite": "<string>",
    "jobTitle": "<string>",
    "address": {
      "addressLine1": "<string>",
      "addressLine2": "<string>",
      "city": "<string>",
      "state": "<string>",
      "country": "<string>",
      "zipCode": "<string>"
    },
    "taxId": "<string>",
    "isEmailVerified": "<boolean>",
    "isTermsAccepted": "<boolean>",
    "isMarketingUpdateFlag": "<boolean>",
    "isProfileComplete": "<boolean>",
    "status": "<integer>",
    "message": "<string>"
}`}
        </pre>
      </ApiEndpointBlock>

      {/* 👇 Swagger-style API block */}
      <ApiEndpointBlock
        method="POST"
        path="/create-an-order"
        description="Create a new domain and workspace order"
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>📦 Payload Example</Typography>
        <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '6px', overflow: 'auto' }}>
          {JSON.stringify(placeOrderPayload, null, 2)}
        </pre>

        <Typography variant="subtitle2" sx={{ mt: 4, mb: 1 }}>🧾 Field Descriptions</Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Field</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Required</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fieldDescriptions.map((field, i) => (
                <TableRow key={i}>
                  <TableCell>{field.field}</TableCell>
                  <TableCell>{field.type}</TableCell>
                  <TableCell>{field.required ? "Yes" : "No"}</TableCell>
                  <TableCell>{field.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography sx={{ mt: 3 }}>💰 <strong>Note:</strong> Mock Balance Threshold: <strong>$200</strong></Typography>
        <div className='d-flex justify-content-between'>
          <TextField
            label="Mock Balance"
            type="number"
            value={balance}
            onChange={(e) => setBalance(parseInt(e.target.value))}
            sx={{ my: 2 }}
            size='small'
          />
        </div>

        {response && (
          <Paper elevation={3} sx={{ mt: 3, p: 2, backgroundColor: '#eee' }}>
            <Typography variant="subtitle2">🧾 Mock Response</Typography>
            <pre>{JSON.stringify(response, null, 2)}</pre>
          </Paper>
        )}

        <Typography sx={{ mt: 3 }}>📌 Expected Responses</Typography>
        <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '6px' }}>
          {`{
  "status": 200,
  "message": "Order Created Success"
}
{
  "status": 201,
  "message": "Insufficient balance to place an order"
}
{
  "status": 502,
  "message": "Invalid Request"
}
{
  "status": 403,
  "message": "API Not Active"
}`}
        </pre>
      </ApiEndpointBlock>

      {/* View ALL Order List */}
      <ApiEndpointBlock
        method="GET"
        path="/viewuserorderperproduct?flat=true&customerId=891&page=0&size=50&status=All"
        description="Retrieve flat list of product orders for a customer"
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>📤 Order & Product List</Typography>

        <div>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 2, color: 'error.main' }}>⚠️ Note: A Valid <strong>Bearer Token</strong> is required in the Authorization header to fetch actual user data. </Typography>
        </div>

        <ul>
          <li><code>flat=true</code> – Return data in flat format</li>
          <li><code>customerId=891</code> – Customer ID whose orders should be fetched</li>
          <li><code>page=0</code> – Pagination: page number</li>
          <li><code>size=50</code> – Pagination: number of records per page</li>
          <li><code>status=All</code> – Order status filter</li>
        </ul>

        <Typography variant="subtitle2" sx={{ mt: 2 }}>📥 Response Example</Typography>
        <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '6px', overflow: 'auto' }}>
          {`{
  "orders": [],
  "flatOrders": [
    {
      "orderId": "<string>",
      "orderNumber": "<string>",
      "orderDate": "<string>",
      "userId": "<string>",
      "userName": "<string>",
      "productId": "<string>",
      "productName": "<string>",
      "organisationName": "<string>",
      "emailProvider": "<string>",
      "domain": "<string>",
      "email": "<string>",
      "firstName": "<string>",
      "lastName": "<string>",
      "profilePicture": "<string>",
      "fwdDomain": "<string>",
      "preferredPassword": "<string>",
      "recoveryEmail": "<string>",
      "emailSendingTool": "<string>",
      "workspace": "<string>",
      "loginEmail": "<string>",
      "loginPassword": "<string>",
      "domainRegistrarURL": "<string>",
      "domainRegistrarEmail": "<string>",
      "domainRegistrarPassword": "<string>",
      "domainRegistrar2FAFlag": "<boolean>",
      "workspaceAdminFlag": "<boolean>",
      "emailFwdFlag": "<boolean>",
      "fwdEmailAddress": "<string>",
      "fwdMailBoxLoginURL": "<string>",
      "fwdMailBoxLogin": "<string>",
      "fwdMailBoxLoginPassword": "<string>",
      "fwdMailBox2FAFlag": "<boolean>",
      "isActive": "<boolean>",
      "createdDate": "<dateTime>",
      "modifiedDate": "<dateTime>",
      "userType": "<string>",
      "status": "<string>"
    }
  ],
  "summary": {},
  "totalCount": "<long>",
  "orderCountByStatus": "<long>",
  "paymentSummery": []
}`}
        </pre>
      </ApiEndpointBlock>

      {/* Order Status API */}
      <ApiEndpointBlock
        method="GET"
        path="/order-status/{orderNumber}"
        description="Get the status of a specific order by order number. Works like a webhook call."
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>🔐 Authorization</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This endpoint requires a valid <strong>Bearer Token</strong> in the <code>Authorization</code> header.
        </Typography>

        <Typography variant="subtitle2">🧾 Sample Request</Typography>
        <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: 6 }}>
          {`GET {{baseUrl}}/order-status/123456789
Authorization: Bearer <your_token_here>`}
        </pre>

        <Typography variant="subtitle2" sx={{ mt: 2 }}>📥 Response Example</Typography>
        <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: 6 }}>
          {`{
  "orderNumber": 123456789,
  "status": "COMPLETED",
  "message": "Order status fetched successfully",
  "modifiedDate": "2025-05-07T10:34:00Z"
}`}
        </pre>

        <Typography variant="subtitle2" sx={{ mt: 2 }}>📛 Error Responses</Typography>
        <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: 6 }}>
          {`{
  "orderNumber": 123456789,
  "status": "NOT_FOUND",
  "message": "You Do Not Have Permission",
  "modifiedDate": "2025-05-07T10:35:00Z"
}

{
  "orderNumber": 123456789,
  "status": "NOT_FOUND",
  "message": "Order not found",
  "modifiedDate": "2025-05-07T10:35:00Z"
}`}
        </pre>
      </ApiEndpointBlock>


    </Box>
  );
};

export default ExposedAPI;
